/** FAQ: Sådan får du mest ud af træningen i ti-fingersystemet. */

export interface FaqItem {
  id: string;
  question: string;
  paragraphs: string[];
}

export interface FaqSection {
  id: string;
  title: string;
  items: FaqItem[];
}

export const TRAINING_FAQ: FaqSection[] = [
  {
    id: 'kom-i-gang',
    title: 'Kom i gang',
    items: [
      {
        id: 'hvad-er-tifinger',
        question: 'Hvad er ti-fingersystemet?',
        paragraphs: [
          'Ti-fingersystemet — også kaldet blindskrift eller touchtyping — betyder, at du skriver med alle ti fingre uden at kigge på tastaturet. Hver finger har sit eget område på tastaturet. Målet er, at bevægelserne bliver automatiske, så du kan fokusere på hvad du skriver, ikke hvor tasterne sidder.',
          'TouchType træner dig trin for trin: først få taster, derefter flere — altid med fokus på at ramme rigtigt.',
        ],
      },
      {
        id: 'hvorfor-laere',
        question: 'Hvorfor er det værd at lære?',
        paragraphs: [
          'Når fingrene kender tastaturet, skriver du typisk hurtigere og med færre fejl. Du slipper for at lede efter bogstaver med blikket — det sparer energi, især når du skriver længere tekster til skole, arbejde eller beskeder.',
          'Det tager tid at lære, men hver kort øvesession lægger et lag oven på det forrige. TouchType er bygget til den langsomme, sikre vej — ikke til at jagte rekorder fra dag ét.',
        ],
      },
      {
        id: 'hvem-kan-laere',
        question: 'Hvem kan lære det?',
        paragraphs: [
          'Børn, unge og voksne kan alle lære ti-fingersystemet. Det vigtigste er tålmodighed og regelmæssig træning — ikke alder.',
          'Har du skrevet på din egen måde i mange år, tager omstillingen længere, men det er stadig muligt. Start med pensum og accepter, at det kan føles langsommere i starten. Det er normalt.',
        ],
      },
      {
        id: 'hvor-lang-tid',
        question: 'Hvor lang tid tager det?',
        paragraphs: [
          'Der findes ingen fast kalender. Mange mærker tydelig fremgang efter et par ugers korte daglige øvelser; flyd skrivning uden at kigge ned kan tage måneder.',
          'TouchType deler læringen op i niveauer og trin, så du altid ved, hvad du arbejder på. Fart kommer, når fingrene kender tasterne — ikke omvendt.',
          'Hint: Sæt dig et realistisk mål i appen (fx «Hverdagstastatur») i stedet for at sammenligne dig med andre.',
        ],
      },
      {
        id: 'hvor-meget-oeve',
        question: 'Hvor meget skal jeg øve om dagen?',
        paragraphs: [
          '10–20 minutter de fleste dage slår én lang session om ugen. Konsistens bygger muskelhukommelse.',
          'TouchType tæller streak, når du gennemfører mindst én lektion på en dag. Det er en venlig påmindelse — ikke en straf. Gået en dag glip af? Bare i gang igen i morgen.',
          'Hint: Én fuldført trin-lektion er bedre end at give op halvvejs fordi du «ikke nåede mere».',
        ],
      },
      {
        id: 'mac-eller-pc',
        question: 'Mac eller PC-tastatur?',
        paragraphs: [
          'Dansk tastatur findes i to layouts: Mac og PC. Tasterne er de samme bogstaver, men visningen i appen matcher det tastatur du sidder ved.',
          'Vælg det rigtige layout under Indstillinger → Tastatur. Det påvirker kun tastaturvisningen og fingervejledning — ikke hvad du skal skrive.',
        ],
      },
      {
        id: 'fysisk-setup',
        question: 'Fysisk setup — stol, skærm og pauser',
        paragraphs: [
          'Sid behageligt med fødderne i gulvet og håndled, der ikke hviler hårdt på bordet. Skærmen i øjenhøjde reducerer træk i nakken. Fingrene skal kunne nå tasterne uden at løfte hele hånden.',
          'Tag korte pauser — især hvis skuldre eller håndled begynder at mærkes. Ti minutters fokuseret træning med pause bagefter er bedre end en time med spænding.',
        ],
      },
    ],
  },
  {
    id: 'grundprincipper',
    title: 'Grundprincipper',
    items: [
      {
        id: 'noejagtighed-foer-fart',
        question: 'Nøjagtighed før fart — hvorfor?',
        paragraphs: [
          'TouchType kræver mindst 95 % nøjagtighed for at bestå et trin. Det er med vilje: fejl, du gentager mange gange, sætter sig i fingrene.',
          'WPM (ord pr. minut) vises som feedback, men du blokeres ikke af lav fart i pensum. Lær først at ramme rigtigt — så kommer hastigheden af sig selv.',
          'Hint: Er du i tvivl mellem «hurtigere» og «færre fejl», vælg altid færre fejl.',
        ],
      },
      {
        id: 'kigge-paa-tastatur',
        question: 'Må jeg kigge på tastaturet?',
        paragraphs: [
          'I starten kan et hurtigt blik være uundgåeligt. Men hver gang du kigger ned, træner du øje–hånd i stedet for finger–hånd.',
          'Praktisk regel: Brug appens fingervejledning, når du er i tvivl — og øv dig i at lade blikket blive på skærmen. Jo længere du kan holde øjnene på teksten, jo tættere er du på ægte blindskrift.',
          'Hint: Sluk eller dæk ikke tastaturet som straf — brug hints og korte gentagelser i stedet.',
        ],
      },
      {
        id: 'ved-fejl',
        question: 'Hvad gør jeg ved fejl?',
        paragraphs: [
          'I TouchType skal du rette med det samme. En forkert tast tæller som fejl og cursor flytter sig ikke, før du rammer rigtigt.',
          'Det føles langsomt, men det lærer fingrene det korrekte mønster. At «løbe videre» og rette bagefter træner dårlige vaner.',
          'Hint: Efter en runde med mange fejl — gentag trinnet eller brug Svage punkter / Vælg selv, i stedet for at skrue hastigheden op.',
        ],
      },
      {
        id: 'klar-til-fart',
        question: 'Hvornår er jeg klar til at gå hurtigere?',
        paragraphs: [
          'Når du kan gennemføre trin med 95 %+ nøjagtighed uden konstant at kigge på tastaturet, og når hints sjældent tænder.',
          'I rytmerunden og metronom-øvelsen kan du så prøve højere tempo — start med appens foreslåede BPM og skru langsomt op.',
          'Hint: Hvis nøjagtigheden falder under 90 %, når du skruer op — sænk tempoet igen. Det er et signal, ikke en fiasko.',
        ],
      },
      {
        id: 'daarlige-vaner',
        question: 'Dårlige vaner — pegefinger-jagt og «mit eget system»',
        paragraphs: [
          'Mange selvlærte skriver med få fingre og flytter hænderne rundt. Det kan være hurtigt på kort sigt, men det begrænser dig på længere sigt.',
          'TouchType lærer dig hjemmerækken (ASDF JKLÆ) og udvider gradvist. Det kan føles som et skridt tilbage i starten. Det er investeringen.',
          'Hint: Undgå at «hjælpe» med pegefingeren på taster, du ikke har lært endnu — vent til pensum introducerer dem.',
        ],
      },
      {
        id: 'overfoersel-hverdag',
        question: 'Overførsel til hverdagsskrivning',
        paragraphs: [
          'Det du øver i appen, skal bruges udenfor appen for at blive fast. Skriv mails, noter eller beskeder med samme fingerplacering — også når det går langsommere.',
          'Hverdagstesten i onboarding (fri skrivning i ét minut) måler netop den slags skrivning. Gentag den i indstillinger, når du vil se fremgang i «virkeligheden».',
        ],
      },
      {
        id: 'motivation-streak',
        question: 'Motivation og streak',
        paragraphs: [
          'Streak tæller dage i træk med mindst én lektion. Det er ment som motivation — ikke som krav om perfektion.',
          'XP og badges er ekstra feedback. Det vigtigste mål er stadig: bestå trin med god nøjagtighed og mærke, at svære taster bliver lettere.',
          'Hint: Har du en travl dag, er én kort lektion nok til at holde streak — og mere værd end at springe over.',
        ],
      },
    ],
  },
  {
    id: 'pensum',
    title: 'Pensum i TouchType',
    items: [
      {
        id: 'niveau-trin-runder',
        question: 'Niveau → trin → runder — hvad betyder det?',
        paragraphs: [
          'Niveau (grundniveau, niveau 1–6): et større kapitel — fx «hele alfabetet» eller «shift og tegn».',
          'Trin: én lektion inden for niveauet — typisk med flere runder.',
          'Runde: én kort øvelse med sin egen tekst og type (synlig tekst, diktat, flash osv.).',
          'Du går trin for trin frem. Ekstra øvelser på dashboardet ligger ved siden af pensum og låser ikke næste trin op.',
        ],
      },
      {
        id: 'bestaa-trin',
        question: 'Hvornår består jeg et trin?',
        paragraphs: [
          'Du består, når du har gennemført alle runder i trinnet, og opnået mindst 95 % nøjagtighed på lektionen som helhed.',
          'Ekstra øvelser har ingen beståelseskrav til progression — du kan øve frit.',
        ],
      },
      {
        id: 'trin-bevis',
        question: 'Hvad er trin-bevis?',
        paragraphs: [
          'Efter lektionens runder kommer en kort bevis-fase (ca. 35 sekunder): du skriver en lille, forudsigelig tekst med de taster, trinnet handlede om.',
          'Du skal igen ramme 95 % nøjagtighed. WPM tæller ikke med her — det er en ren «kan du det stadig?»-test.',
          'Består du, ser du Trin bestået og kan gå videre. Fejler du, kan du gentage kun beviset eller hele trinnet.',
          'Hint: Lektionen kan føles klaret, men beviset fanger, om fingrene virkelig husker det — tag det roligt.',
        ],
      },
      {
        id: 'mini-checkpoints',
        question: 'Mini-checkpoints',
        paragraphs: [
          'Mellem visse trin møder du en mini-checkpoint: en kort port med særlig ceremoni (fx «Port bestået»). Det markerer, at du er midt i et større niveau — ikke bare «endnu en runde».',
          'De er valgfrie at bestå for at komme videre i pensum, men giver et tydeligt win undervejs.',
        ],
      },
      {
        id: 'palette-cleanser',
        question: '«Palette cleanser»-runder — hvorfor skifter øvelsen?',
        paragraphs: [
          'Nogle trin har én runde, der bryder mønsteret — fx diktat, flash eller rytme midt i ellers ensartede afskriftsrunder. Det holder hovedet vågent og træner samme taster på en ny måde.',
          'Hint: Oplever du trin som «mere af det samme», så kig efter om næste runde er en særlig type — den er ofte den, der varierer.',
        ],
      },
      {
        id: 'rundetyper',
        question: 'Forskellige rundetyper — kort overblik',
        paragraphs: [
          'Guided: tekst på skærmen; stærkere hjælp i starten.',
          'Recall: tekst på skærmen; hints ved tøven.',
          'Diktat: lyt og skriv uden (eller med) synlig tekst.',
          'Flash: ord vises kort, så skriver du.',
          'Metronom: skriv i takt med et tempo.',
          'Skygge: tekst bevæger sig — følg tempoet.',
          'Echo: kort chunk vises og forsvinder.',
          'Fortælling: historie med huller du udfylder.',
          'Ikke alle typer findes i hvert trin — pensum vælger, hvad der passer til, hvor langt du er.',
        ],
      },
      {
        id: 'trin-foles-ens',
        question: 'Hvorfor føles mange trin ens?',
        paragraphs: [
          'De fleste trin handler om det samme grundlag: læse tekst → skrive korrekt → næste runde. Det er med vilje — gentagelse bygger automatik.',
          'Appen modvirker monotonien med trin-bevis, mini-checkpoints, palette cleansers og ekstra øvelser (diktat, fortælling, svage punkter). Brug dem, når du har brug for variation.',
        ],
      },
      {
        id: 'xp',
        question: 'Hvad betyder XP?',
        paragraphs: [
          'XP er erfaringspoint fra lektioner — ikke «antal bogstaver» direkte, men en blanding af grundpoint for gennemført lektion, bonus for høj nøjagtighed, lidt bonus for fart (op til et loft), og ekstra hvis alle runder er bestået.',
          'XP er motivation og overblik — progression i pensum styres af beståede trin, ikke XP alene.',
        ],
      },
    ],
  },
  {
    id: 'fingre-hints',
    title: 'Fingre og hints',
    items: [
      {
        id: 'hjemmeraekke',
        question: 'Hjemmerækken og fingerplacering',
        paragraphs: [
          'Venstre: lillefinger på A, ring på S, lang på D, pege på F. Højre: pege på J, lang på K, ring på L, lille på Æ. Tommelfingre på mellemrumstasten.',
          'Find F og J — der er ofte små markeringer på tastaturet. Resten af fingrene hører til «deres» kolonne og række.',
          'TouchType viser farver på tastaturet, så du kan se, hvilken finger der hører til hvilken tast.',
        ],
      },
      {
        id: 'fingervejledning',
        question: 'Fingervejledning på skærmen',
        paragraphs: [
          'Blød hint: den forventede tast fremhæves, når du tøver (~1,5 sek.).',
          'Stærk hint: tydeligere markering og finger-navn (~3 sek.).',
          'I challenge-tilstand kommer hints først efter flere fejl — så du ikke får hjælp for tidligt.',
          'Hint: Brug hints som lærer i starten; prøv at klare senere runder med færre aktiverede hints ved at holde et jævnt, roligt tempo.',
        ],
      },
      {
        id: 'mental-forberedelse',
        question: 'Mental forberedelse',
        paragraphs: [
          'Før trin med nye taster kan du få mental forberedelse: 15 sekunder, hvor tastaturet viser nye taster én ad gangen — uden at du skriver. Du skal forestille dig fingerbevægelsen.',
          'Det kan slås til/fra under Indstillinger → Lyd og hjælp → Mental forberedelse.',
          'Hint: Brug de 15 sekunder — spring ikke over, første gang du ser en helt ny tast.',
        ],
      },
      {
        id: 'hints-fra',
        question: 'Hvornår skal hints slås fra?',
        paragraphs: [
          'Der er ikke en knap «sluk alle hints» — de kommer ved tøven og fejl. Du «slår dem fra» ved at skrive flydende nok til at de sjældent aktiveres.',
          'Når du sjældent ser stærk hint, er du klar til mere udfordring (højere diktat-enheder, rytme, ekstra øvelser).',
        ],
      },
      {
        id: 'mellemrum',
        question: 'Mellemrum efter ord — «gå videre»',
        paragraphs: [
          'I mange runder skal du trykke mellemrum, når et ord er færdigt — også i flash og diktat. Det markerer ordgrænsen og ligner rigtig skrivning.',
          'Ser du ikke, at du skal videre, så prøv mellemrum. Appen viser ofte en lille påmindelse om det.',
        ],
      },
      {
        id: 'shift',
        question: 'Shift og store bogstaver',
        paragraphs: [
          'Fra niveau 5 skal store og små bogstaver matches præcist (strict case). Du lærer shift på hjemmerækken, sætningsstart og tegn som ? og !.',
          'Hint: Hold shift med den modsatte hånds lillefinger/pegefinger — ikke begge hænder væk fra hjemmerækken på én gang.',
        ],
      },
    ],
  },
  {
    id: 'hastighed-maal',
    title: 'Hastighed og mål',
    items: [
      {
        id: 'wpm',
        question: 'Hvad er WPM?',
        paragraphs: [
          'WPM = words per minute (ord pr. minut). I TouchType beregnes det ud fra korrekte tegn og tid — ca. ét ord = 5 tegn.',
          'Det er en simpel målestok til sammenligning over tid — ikke en eksamen i sig selv.',
        ],
      },
      {
        id: 'baseline',
        question: 'Afskriftstest vs. hverdagstest',
        paragraphs: [
          'Afskriftstest: skriv tekst du ser på skærmen — måler klassisk touchtyping.',
          'Hverdagstest: skriv frit i ét minut uden forlæg — tættere på mails og beskeder.',
          'Mange scorer lavere på hverdagstest. Begge er nyttige; vælg det mål i appen, der matcher, hvad du vil bruge blindskrift til.',
        ],
      },
      {
        id: 'maal-arketyper',
        question: 'Mål-arketyper',
        paragraphs: [
          'Under Indstillinger → Dit mål vælger du en profil — fx Rolig skriver, Hverdagstastatur, Arbejdsrytme, Fokusflow eller Touch-virtuos.',
          'Hver profil sætter foreslået WPM, nøjagtighed og kurriculum-fremgang. Det er retning, ikke en daglig karakter.',
        ],
      },
      {
        id: 'udvikling-over-tid',
        question: 'WPM-check-ins og udvikling over tid',
        paragraphs: [
          'Dashboard og grafer viser din udvikling — bl.a. WPM og aktivitet dag for dag, når du har øvet.',
          'Brug det til at se tendenser over uger, ikke til at stress over én dårlig dag.',
          'Hint: Sammenlign dig med dit eget udgangspunkt (baseline), ikke med online-rekorder.',
        ],
      },
      {
        id: 'aktivitet-log',
        question: 'Tegn og aktivitet — hvad logges?',
        paragraphs: [
          'Appen tæller antal tastetryk og korrekte tegn over tid og logger øvelser (pensum vs. ekstra). Det giver mening i «træning over tid»-diagrammet.',
          'Det er til dit overblik — ikke en offentlig rangliste.',
        ],
      },
      {
        id: 'gentag-baseline',
        question: 'Hvornår skal jeg gentage baseline?',
        paragraphs: [
          'Gentag afskrift eller hverdagstest i indstillinger, når du har gennemført et helt niveau eller føler tydelig fremgang, du vil opdatere dit mål-arketype, eller du er nysgerrig på, om hverdagshastigheden er steget.',
          'Baseline overskriver det gemte udgangspunkt — det er bevidst, så dit mål følger dig.',
        ],
      },
    ],
  },
  {
    id: 'rundetyper',
    title: 'Rundetyper',
    items: [
      {
        id: 'afskrivning',
        question: 'Synlig tekst (afskrivning)',
        paragraphs: [
          'Den mest almindelige runde: tekst står foran dig, du skriver den.',
          'Hints: Kig på tekstlinjen, ikke tastaturet. Sig bogstavet højt i hovedet, mens fingeren bevæger sig. Roligt tempo slår hurtige fejl.',
        ],
      },
      {
        id: 'orddiktat',
        question: 'Orddiktat (i pensum og som ekstra)',
        paragraphs: [
          'Du lytter og skriver — ofte med prikker for hele sætningen/enheden, som du udfylder ord for ord.',
          'Hints: Lyt til hele enheden før du starter. I indstillinger: vælg enhedslængde (1–7 ord) — start med 1–2, gå op når det sitter. Juster oplæsningshastighed (0,80–0,90 for begyndere). Vælg dansk/engelsk på forhånd i ekstraøvelsens pre-skærm.',
        ],
      },
      {
        id: 'tegn-uden-diktat',
        question: 'Diktat uden tegn — komma og punktum',
        paragraphs: [
          'Komma, punktum og bindestreg hører ikke tydeligt til i oplæsning. Derfor bruger Øverunde: Komma og punktum synlig tekst — ikke diktat.',
          'Hint: Når du træner tegnsætning, fokuser på flow og placering (lillefinger ned til punktum osv.), ikke på at «høre» tegnet.',
        ],
      },
      {
        id: 'flash',
        question: 'Flash-runde',
        paragraphs: [
          'Ord eller korte stykker vises kort og forsvinder — du skriver fra hukommelsen.',
          'Hints: Læs chunk’en mens den vises; start ikke før du har set hele ordet. Husk mellemrum mellem ord. Korte ord går hurtigt — det er ok at være langsom i starten; nøjagtighed først.',
        ],
      },
      {
        id: 'metronom',
        question: 'Rytmerunde og metronom',
        paragraphs: [
          'Du skriver i takt med et metronom (visuelt og evt. lyd).',
          'Hints: Brug det foreslåede tempo først (stiger med dit niveau, ca. 55–70 BPM). Du kan vælge 50–120 BPM selv — skru ned, hvis nøjagtigheden falder. Metronom er træning i jævn rytme, ikke maksimal fart.',
        ],
      },
      {
        id: 'fortaelling-1',
        question: 'Fortælling 1',
        paragraphs: [
          'Interaktiv lydhistorie: du lytter og skriver kun ord i huller (navne, ting du vælger). Resten oplæses.',
          'Hints: Tilgængelig efter niveau 3. Vælg ord, der giver mening for dig — du skal stadig skrive dem korrekt bagefter. God til engagement, når almindelige runder føles tørre.',
        ],
      },
      {
        id: 'fortaelling-2',
        question: 'Fortælling 2',
        paragraphs: [
          'Historien står på skærmen; du skriver med. Undervejs udfylder du blanks (navn, dyr, steder), og historien fortsætter — ofte med AI, hvis serveren er sat op.',
          'Hints: Læs som en almindelig afskriftsrunde; blanks er korte pauser i flowet. Historien kan blive lang — hold øje med nøjagtighed, ikke fart. Kræver kørende server til fuld AI; ellers demo-tilstand.',
        ],
      },
      {
        id: 'fortaelling-3',
        question: 'Fortælling 3',
        paragraphs: [
          'Du skriver en hel fortælling selv — uden huller eller oplæsning. Når du er færdig, vælger du feedback: korrektur (stavning og tegnsætning), vurdering (styrker og forbedringer) eller et fortsættelsesspørgsmål.',
          'Hints: Enter giver ny linje. Skriv mindst ca. 50 tegn før «Jeg er færdig». Du kan skrive videre på samme tekst efter feedback. Kræver kørende server til fuld AI; ellers demo-tilstand. Tilgængelig efter niveau 3.',
        ],
      },
      {
        id: 'svage-punkter-runde',
        question: 'Svage punkter',
        paragraphs: [
          'Ekstraøvelse der analyserer dine fejl, langsomme taster, svære ord og bogstav-par (bigrams) fra tidligere lektioner.',
          'Hints: Læs startskærmen — den forklarer, hvad der er svagt og hvorfor. Brug den efter et hårdt trin, ikke i stedet for pensum. Vises først, når appen har nok data og du har gennemført grundniveau.',
        ],
      },
    ],
  },
  {
    id: 'ekstra-ovelser',
    title: 'Ekstra øvelser',
    items: [
      {
        id: 'overblik-ekstra',
        question: 'Overblik over ekstraøvelser',
        paragraphs: [
          'På dashboard under Ekstra øvelser finder du bl.a. metronom/skygge (efter niveau 1/2), orddiktat (efter niveau 1), imaginær skrivning (efter niveau 1), fortælling 1, 2 og 3 (efter niveau 3), Skriveleg (efter niveau 3), svage punkter (grundniveau + nok fejldata), vælg selv (efter niveau 1), komma og punktum (efter niveau 4, trin 3), bonus tal og @ (efter niveau 6).',
          'De tæller ikke som krav for næste trin — de er til forstærkning og variation.',
        ],
      },
      {
        id: 'svage-punkter-hvornaar',
        question: 'Svage punkter — hvornår?',
        paragraphs: [
          'Når du har konkrete taster eller kombinationer, der fejler igen og igen — eller når en lektion føltes «næsten», men ikke helt.',
          'Ikke som erstatning for at gennemføre pensum: bestå trinnet først, så polér med svage punkter.',
        ],
      },
      {
        id: 'komma-punktum-oeverunde',
        question: 'Øverunde: Komma og punktum',
        paragraphs: [
          'Korte sætninger og lister med , . i realistiske mønstre — fx «Ko. Mus.» og indkøbslister.',
          'Hints: Kun dansk, uden diktat — se teksten. Gentag gerne flere gange; teksterne varierer mellem sessioner. Perfekt, hvis niveau 4 føltes for hurtigt på tegnsætning.',
        ],
      },
      {
        id: 'vaelg-selv',
        question: 'Vælg selv (custom)',
        paragraphs: [
          'Vælg 1–5 taster på et tastatur — appen laver en blandet øvelse med netop dem.',
          'Hints: Valget gemmes til næste gang. Diktat bruges, medmindre du har valgt , . eller - (tegn hører ikke godt til i diktat). Ideel til ét svært bogstav eller en kombination du vil polere.',
        ],
      },
      {
        id: 'imaginaer-skrivning',
        question: 'Imaginær skrivning',
        paragraphs: [
          'Ti korte ord (højst 4 bogstaver) fra dansk/engelsk pulje og pensum — valgt med bred bogstav-dækning, så du kan forestille hele ordet. Tre runder: se ordet, tre sekunders blink, diktat.',
          'Ingen WPM eller tidspres — kun rolig mental forberedelse. Lange ord hører til andre øvelser.',
          'Hint: Forestil fingerløbet for hele ordet — ikke bogstav for bogstav. Føles det for tungt, er ordet for langt til denne øvelse.',
        ],
      },
      {
        id: 'orddiktat-ekstra',
        question: 'Orddiktat som ekstra',
        paragraphs: [
          'Samme koncept som i pensum, men du styrer sværhedsgrad: slider 1–7 ord per enhed (hele sætninger oplæses som én enhed), dansk/engelsk vælges på pre-skærmen, og ordene passer til de taster, du har låst op for.',
          'Hint: Øg antal ord, når 1–2 ord føles let med høj nøjagtighed.',
        ],
      },
      {
        id: 'bonus-tal-email',
        question: 'Bonus: Tal og @',
        paragraphs: [
          'Efter niveau 6: særskilt træning i talrækken og @ / e-mail (med Alt/Option på Mac).',
          'Hint: Tag dem, når du bruger cifre og @ i hverdagen — ikke under alfabet-læring.',
        ],
      },
      {
        id: 'engelsk-niveau-4',
        question: 'Engelske ord i niveau 4',
        paragraphs: [
          'I niveau 4 kan du slå engelsk ordforråd til i indstillinger. Engelsk har mange ord med de bogstaver, du lige har lært — god til variation.',
          'Hint: Vælg enten dansk eller engelsk for hele niveau 4 — ikke begge på én gang. Valget huskes, til du skifter tilbage.',
        ],
      },
    ],
  },
  {
    id: 'foraeldre-konto',
    title: 'Forældre og konto',
    items: [
      {
        id: 'sammen-med-barn',
        question: 'Kan man bruge appen sammen med barnet?',
        paragraphs: [
          'Ja. Opret en brugerprofil per barn (eller brug lokalt uden konto). Følg pensum sammen første gange — især hjemmerække og «kig på skærmen, ikke tastaturet».',
          'Hints til voksne der hjælper: Ros nøjagtighed, ikke fart. Korte sessioner (10–15 min). Lad barnet bestå trin-bevis selv — det giver ejerskab.',
        ],
      },
      {
        id: 'lektion-vs-ekstra',
        question: 'Forskel på lektion og ekstraøvelse',
        paragraphs: [
          'Lektion (pensum): lærer næste trin, låser bogstaver op, kræver 95 % + trin-bevis. Ekstra øvelse: forstærke, variere, målrette — valgfri, ingen progression.',
          'Barnet bør primært følge Din vej mod målet / næste trin; ekstra er dessert, ikke hovedret.',
        ],
      },
      {
        id: 'data-konto',
        question: 'Data, konto og sync',
        paragraphs: [
          'Uden konto: data på enheden (localStorage) eller anonym session. Med konto: e-mail + krypteret adgangskode; sync via server (EU). Gemmes bl.a. trin, XP, streak, fejl per tast, lektionsresultater.',
          'Du kan læse mere under Privatliv på forsiden. Kontakt for sletning af konto.',
          'Hint: Familie med flere brugere — én profil per person, så statistik og svage punkter ikke blandes.',
        ],
      },
    ],
  },
];
